import { useState } from "react";
import {
  OrderItemStatus,
} from "@/redux/services/order/order.type";
import {
  useUpdateOrderItemStatusMutation,
  useUpdateComboItemStatusMutation,
  useUpdateFormulaItemStatusMutation,
} from "@/redux/services/order/order.api";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";
import { toastApiError } from "@/lib/apiErrors";

type Selection = { name: string; options: string[] };

interface KdsItemRowProps {
  itemId: string;
  name: string;
  quantity: number;
  itemStatus: OrderItemStatus;
  orderId: string;
  isCombo?: boolean;
  isFormula?: boolean;
  options?: string[];
  selections?: Selection[];
  note?: string | null;
  onStatusChange?: () => void;
}

const STATUS_PROGRESSION: Record<OrderItemStatus, OrderItemStatus | null> = {
  pending: "preparing",
  preparing: "ready",
  ready: null,
};

const KdsItemRow: React.FC<KdsItemRowProps> = ({
  itemId,
  name,
  quantity,
  itemStatus,
  orderId,
  isCombo = false,
  isFormula = false,
  options,
  selections,
  note,
  onStatusChange,
}) => {
  const [updateOrderItemStatus] = useUpdateOrderItemStatusMutation();
  const [updateComboItemStatus] = useUpdateComboItemStatusMutation();
  const [updateFormulaItemStatus] = useUpdateFormulaItemStatusMutation();
  const [isUpdating, setIsUpdating] = useState(false);

  const mutation = isFormula
    ? updateFormulaItemStatus
    : isCombo
      ? updateComboItemStatus
      : updateOrderItemStatus;

  const next = STATUS_PROGRESSION[itemStatus];
  const isReady = itemStatus === "ready";
  const isPreparing = itemStatus === "preparing";

  const handleAdvance = async () => {
    if (!next || isUpdating) return;
    setIsUpdating(true);
    try {
      await mutation({
        orderUuid: orderId,
        itemUuid: itemId,
        status: next,
      }).unwrap();
      onStatusChange?.();
    } catch (error) {
      // Nothing else reports a failed call: tell the cook (SFRESTO-163).
      toastApiError(error, "Impossible de mettre à jour l'article");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleAdvance}
      disabled={!next || isUpdating}
      aria-label={
        isReady
          ? `${name} prêt`
          : isPreparing
            ? `Marquer ${name} comme prêt`
            : `Démarrer la préparation de ${name}`
      }
      className={cn(
        "w-full flex items-start gap-3 px-3 py-3 text-left rounded-lg",
        "min-h-[64px] transition-colors",
        next && !isUpdating && "active:bg-muted cursor-pointer",
        isReady && "cursor-default opacity-50",
        isPreparing && "bg-warning/5",
      )}
    >
      <span
        className={cn(
          "mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isReady && "bg-success border-success text-white",
          isPreparing && "border-warning text-warning",
          !isReady && !isPreparing && "border-muted-foreground/40",
        )}
      >
        {isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isReady ? (
          <Check className="h-5 w-5" strokeWidth={3} />
        ) : isPreparing ? (
          <span className="h-3 w-3 rounded-full bg-warning animate-pulse" />
        ) : null}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          {quantity > 1 && (
            <span
              className={cn(
                "text-xl font-extrabold tabular-nums leading-tight",
                isReady ? "text-muted-foreground line-through" : "text-primary",
              )}
            >
              {quantity}×
            </span>
          )}
          <span
            className={cn(
              "text-xl font-bold leading-tight",
              isReady ? "line-through" : "text-card-foreground",
            )}
          >
            {name}
          </span>
        </div>

        {selections && selections.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {selections.map((s, i) => (
              <li key={i} className="text-base text-muted-foreground leading-snug">
                <span className="text-card-foreground/70">›</span>{" "}
                {s.name}
                {s.options.length > 0 && (
                  <ul className="mt-0.5 ml-5 space-y-0.5">
                    {s.options.map((opt, j) => (
                      <li
                        key={j}
                        className="text-sm text-muted-foreground/80 leading-snug"
                      >
                        {opt}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}

        {options && options.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {options.map((opt, i) => (
              <li
                key={i}
                className="text-base text-muted-foreground leading-snug"
              >
                {opt}
              </li>
            ))}
          </ul>
        )}

        {note && (
          <div className="mt-2 rounded-md bg-yellow-200 border-l-4 border-yellow-500 px-3 py-2">
            <p className="text-base font-semibold text-yellow-900 leading-snug">
              {note}
            </p>
          </div>
        )}
      </div>
    </button>
  );
};

export default KdsItemRow;
